import { supabase } from './supabaseClient';

export interface MarketData {
  id: string;
  product_name: string;
  category: string;
  location: string;
  price: number;
  unit: string;
  quantity: number;
  quality: 'premium' | 'standard' | 'basic';
  source: 'marketplace' | 'external' | 'government' | 'mock' | 'news' | 'ncpb';
  date: string;
  trend: 'up' | 'down' | 'stable';
  demand_level: 'high' | 'medium' | 'low';
  supply_level: 'high' | 'medium' | 'low';
}

export interface MarketTrend {
  product_name: string;
  category: string;
  location: string;
  current_price: number;
  previous_price: number;
  price_change: number;
  price_change_percentage: number;
  trend_direction: 'up' | 'down' | 'stable';
  demand_trend: 'increasing' | 'decreasing' | 'stable';
  supply_trend: 'increasing' | 'decreasing' | 'stable';
  forecast: {
    next_week: number;
    next_month: number;
    confidence: number;
  };
}

export interface MarketComparison {
  product_name: string;
  category: string;
  locations: {
    location: string;
    price: number;
    quantity: number;
    demand_level: string;
    supply_level: string;
  }[];
  best_price_location: string;
  best_price: number;
  price_variance: number;
  recommendation: string;
}

export interface DemandForecast {
  product_name: string;
  category: string;
  location: string;
  current_demand: number;
  forecasted_demand: {
    next_week: number;
    next_month: number;
    next_quarter: number;
  };
  factors: string[];
  confidence: number;
}

export class MarketIntelligenceService {
  // Get market data for a specific product and location
  static async getMarketData(
    productName?: string,
    location?: string,
    category?: string,
    limit: number = 50
  ): Promise<MarketData[]> {
    try {
      let query = supabase
        .from('market_data')
        .select('*')
        .order('date', { ascending: false });

      if (productName) {
        query = query.ilike('product_name', `%${productName}%`);
      }

      if (location) {
        query = query.eq('location', location);
      }

      if (category) {
        query = query.eq('category', category);
      }

      const { data, error } = await query.limit(limit);

      if (error) {
        console.error('Error fetching market data:', error);
        return this.getMockMarketData();
      }

      if (!data || data.length === 0) {
        return this.getMockMarketData();
      }

      return data;
    } catch (error) {
      console.error('Error fetching market data:', error);
      return this.getMockMarketData();
    }
  }

  // Get market trends for products
  static async getMarketTrends(
    productName?: string,
    location?: string,
    category?: string
  ): Promise<MarketTrend[]> {
    try {
      // In a real implementation, this would analyze historical data
      // For now, we'll return mock trends
      const mockTrends: MarketTrend[] = [
        {
          product_name: 'Maize',
          category: 'Grains',
          location: 'Nairobi',
          current_price: 2500,
          previous_price: 2300,
          price_change: 200,
          price_change_percentage: 8.7,
          trend_direction: 'up',
          demand_trend: 'increasing',
          supply_trend: 'stable',
          forecast: {
            next_week: 2600,
            next_month: 2800,
            confidence: 0.75
          }
        },
        {
          product_name: 'Tomatoes',
          category: 'Vegetables',
          location: 'Mombasa',
          current_price: 120,
          previous_price: 150,
          price_change: -30,
          price_change_percentage: -20,
          trend_direction: 'down',
          demand_trend: 'stable',
          supply_trend: 'increasing',
          forecast: {
            next_week: 110,
            next_month: 100,
            confidence: 0.8
          }
        },
        {
          product_name: 'Beef',
          category: 'Livestock',
          location: 'Kisumu',
          current_price: 450,
          previous_price: 420,
          price_change: 30,
          price_change_percentage: 7.1,
          trend_direction: 'up',
          demand_trend: 'increasing',
          supply_trend: 'decreasing',
          forecast: {
            next_week: 460,
            next_month: 480,
            confidence: 0.7
          }
        }
      ];

      return mockTrends.filter(trend => {
        if (productName && !trend.product_name.toLowerCase().includes(productName.toLowerCase())) {
          return false;
        }
        if (location && trend.location !== location) {
          return false;
        }
        if (category && trend.category !== category) {
          return false;
        }
        return true;
      });
    } catch (error) {
      console.error('Error fetching market trends:', error);
      return [];
    }
  }

  // Compare prices across different markets
  static async getMarketComparison(
    productName: string,
    category?: string
  ): Promise<MarketComparison[]> {
    try {
      const marketData = await this.getMarketData(productName);
      
      // Group by product and category
      const groupedData = marketData.reduce((acc, item) => {
        const key = `${item.product_name}-${item.category}`;
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(item);
        return acc;
      }, {} as Record<string, MarketData[]>);

      const comparisons: MarketComparison[] = [];

      for (const [key, items] of Object.entries(groupedData)) {
        const [product_name, category] = key.split('-');
        
        const locations = items.map(item => ({
          location: item.location,
          price: item.price,
          quantity: item.quantity,
          demand_level: item.demand_level,
          supply_level: item.supply_level
        }));

        const prices = items.map(item => item.price);
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);
        const priceVariance = ((maxPrice - minPrice) / minPrice) * 100;

        const bestPriceItem = items.find(item => item.price === minPrice);

        comparisons.push({
          product_name,
          category,
          locations,
          best_price_location: bestPriceItem?.location || '',
          best_price: minPrice,
          price_variance: priceVariance,
          recommendation: this.generateRecommendation(items)
        });
      }

      return comparisons;
    } catch (error) {
      console.error('Error generating market comparison:', error);
      return [];
    }
  }

  // Get demand forecasts
  static async getDemandForecasts(
    productName?: string,
    location?: string,
    category?: string
  ): Promise<DemandForecast[]> {
    try {
      // Mock demand forecasts
      const mockForecasts: DemandForecast[] = [
        {
          product_name: 'Maize',
          category: 'Grains',
          location: 'Nairobi',
          current_demand: 1000,
          forecasted_demand: {
            next_week: 1100,
            next_month: 1200,
            next_quarter: 1400
          },
          factors: ['Seasonal demand increase', 'Population growth', 'Export opportunities'],
          confidence: 0.8
        },
        {
          product_name: 'Tomatoes',
          category: 'Vegetables',
          location: 'Mombasa',
          current_demand: 500,
          forecasted_demand: {
            next_week: 480,
            next_month: 450,
            next_quarter: 400
          },
          factors: ['Seasonal decline', 'Competition from imports', 'Weather conditions'],
          confidence: 0.7
        }
      ];

      return mockForecasts.filter(forecast => {
        if (productName && !forecast.product_name.toLowerCase().includes(productName.toLowerCase())) {
          return false;
        }
        if (location && forecast.location !== location) {
          return false;
        }
        if (category && forecast.category !== category) {
          return false;
        }
        return true;
      });
    } catch (error) {
      console.error('Error fetching demand forecasts:', error);
      return [];
    }
  }

  // Get supply chain optimization recommendations
  static async getSupplyChainRecommendations(
    productName: string,
    sourceLocation: string,
    targetLocation: string
  ): Promise<{
    optimal_route: string[];
    estimated_cost: number;
    estimated_time: number;
    risk_factors: string[];
    recommendations: string[];
  }> {
    try {
      // Mock supply chain recommendations
      return {
        optimal_route: [sourceLocation, 'Nairobi', targetLocation],
        estimated_cost: 15000,
        estimated_time: 48, // hours
        risk_factors: ['Weather conditions', 'Road conditions', 'Market volatility'],
        recommendations: [
          'Use refrigerated transport for perishables',
          'Consider alternative routes during rainy season',
          'Monitor fuel prices for cost optimization',
          'Establish backup suppliers'
        ]
      };
    } catch (error) {
      console.error('Error generating supply chain recommendations:', error);
      return {
        optimal_route: [],
        estimated_cost: 0,
        estimated_time: 0,
        risk_factors: [],
        recommendations: []
      };
    }
  }

  // Get market alerts and notifications
  static async getMarketAlerts(
    userId: string,
    preferences?: {
      products?: string[];
      locations?: string[];
      price_threshold?: number;
    }
  ): Promise<{
    id: string;
    type: 'price_spike' | 'price_drop' | 'demand_increase' | 'supply_shortage';
    product_name: string;
    location: string;
    message: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    created_at: string;
  }[]> {
    try {
      // Mock market alerts
      const mockAlerts = [
        {
          id: '1',
          type: 'price_spike' as const,
          product_name: 'Maize',
          location: 'Nairobi',
          message: 'Maize prices have increased by 15% in the last week',
          severity: 'high' as const,
          created_at: new Date().toISOString()
        },
        {
          id: '2',
          type: 'demand_increase' as const,
          product_name: 'Tomatoes',
          location: 'Mombasa',
          message: 'High demand for tomatoes expected due to upcoming festival',
          severity: 'medium' as const,
          created_at: new Date().toISOString()
        }
      ];

      return mockAlerts;
    } catch (error) {
      console.error('Error fetching market alerts:', error);
      return [];
    }
  }

  // Generate recommendation based on market data
  private static generateRecommendation(items: MarketData[]): string {
    const avgPrice = items.reduce((sum, item) => sum + item.price, 0) / items.length;
    const priceVariance = Math.max(...items.map(item => item.price)) - Math.min(...items.map(item => item.price));
    
    if (priceVariance > avgPrice * 0.3) {
      return 'High price variance detected. Consider arbitrage opportunities.';
    } else if (items.some(item => item.demand_level === 'high' && item.supply_level === 'low')) {
      return 'High demand, low supply detected. Consider increasing production.';
    } else if (items.some(item => item.demand_level === 'low' && item.supply_level === 'high')) {
      return 'Low demand, high supply detected. Consider diversifying or reducing production.';
    } else {
      return 'Market conditions are stable. Monitor for changes.';
    }
  }

  // Mock market data for fallback
  private static getMockMarketData(): MarketData[] {
    return [
      {
        id: '1',
        product_name: 'Maize',
        category: 'Grains',
        location: 'Nairobi',
        price: 2500,
        unit: 'kg',
        quantity: 1000,
        quality: 'standard',
        source: 'mock',
        date: new Date().toISOString(),
        trend: 'up',
        demand_level: 'high',
        supply_level: 'medium'
      },
      {
        id: '2',
        product_name: 'Tomatoes',
        category: 'Vegetables',
        location: 'Mombasa',
        price: 120,
        unit: 'kg',
        quantity: 500,
        quality: 'premium',
        source: 'mock',
        date: new Date().toISOString(),
        trend: 'down',
        demand_level: 'medium',
        supply_level: 'high'
      },
      {
        id: '3',
        product_name: 'Beef',
        category: 'Livestock',
        location: 'Kisumu',
        price: 450,
        unit: 'kg',
        quantity: 200,
        quality: 'premium',
        source: 'mock',
        date: new Date().toISOString(),
        trend: 'up',
        demand_level: 'high',
        supply_level: 'low'
      }
    ];
  }

  // Subscribe to market updates
  static subscribeToMarketUpdates(
    callback: (update: MarketData) => void
  ): () => void {
    const subscription = supabase
      .channel('market_updates')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'market_data'
      }, (payload) => {
        callback(payload.new as MarketData);
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }
} 
